import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { captureApiError } from './api/capture-error';
import { ApiError, clearAuth, ensureFreshAccessToken, getAccessToken, redirectToLogin, } from './api/client';
// Default fetcher for React Query
async function defaultQueryFn({ queryKey }) {
    const endpoint = queryKey[0];
    // The shared, single-flight refresh apiFetch uses: queries that mount together after the
    // access token expires make ONE refresh call between them, not one each, and only an answer
    // that says the session is over signs the user out — a 5xx or a network blip rejects with a
    // retryable ApiError and leaves the session alone.
    await ensureFreshAccessToken();
    const headers = {
        'Content-Type': 'application/json',
    };
    const token = getAccessToken();
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(endpoint, {
        headers,
        credentials: 'include',
    });
    if (!response.ok) {
        if (response.status === 401) {
            clearAuth();
            redirectToLogin();
        }
        throw new ApiError(response.status, await response.text());
    }
    const text = await response.text();
    return text ? JSON.parse(text) : {};
}
/**
 * Retry predicate factory: retries up to `maxRetries` times, except for
 * terminal client errors (4xx) which are never transient — a retry re-issues
 * the same request against the same resource and gets the same failure (a
 * deleted study's 404 doesn't become a 200 on attempt two), it just burns
 * requests and delays the error UI. 408 (Request Timeout) and 429 (Too Many
 * Requests) are the exceptions — those are meant to be retried. This is the
 * single source of truth for that exclusion; anywhere a query needs a
 * `retry` override (e.g. a shorter-than-default max) should build it from
 * this helper instead of passing a bare number, which would silently defeat
 * the exclusion (a numeric `retry` in React Query never inspects the error).
 */
export function retryUnlessTerminalError(maxRetries) {
    return (failureCount, error) => {
        if (error instanceof ApiError &&
            error.status >= 400 &&
            error.status < 500 &&
            error.status !== 408 &&
            error.status !== 429) {
            return false;
        }
        return failureCount < maxRetries;
    };
}
export const queryClient = new QueryClient({
    // React Query swallows rejections once a component handles the error state,
    // so failed API calls never reach Sentry's unhandled-rejection hook. Capture
    // them here centrally (network failures and 5xx only — see capture-error.ts).
    queryCache: new QueryCache({
        onError: (error, query) => captureApiError(error, { source: 'query', key: String(query.queryKey[0] ?? '') }),
    }),
    mutationCache: new MutationCache({
        onError: (error, _variables, _context, mutation) => captureApiError(error, {
            source: 'mutation',
            key: mutation.options.mutationKey ? String(mutation.options.mutationKey[0]) : undefined,
        }),
    }),
    defaultOptions: {
        queries: {
            queryFn: defaultQueryFn,
            staleTime: 5 * 60 * 1000, // 5 minutes
            retry: retryUnlessTerminalError(3),
        },
    },
});
